import { useState, useMemo, useEffect } from 'react';
import type { Order, PaymentType } from '@/types/common';
import { formatCurrency } from '@/utils/format';

export interface RegularPaymentProps {
  order: Order;
  method: PaymentType;
  status: 'idle' | 'processing' | 'completed' | 'error';
  onSubmit: (paymentDetails: {
    payment_type: PaymentType;
    amount: number;
    tip_amount: number;
    custom_amount: number;
    received?: number;
    cardDetails?: {
      number: string;
      expiry: string;
      cvc: string;
    };
  }) => Promise<void>;
}

export default function RegularPayment({ order, method, status, onSubmit }: RegularPaymentProps) {
  const [tipAmount, setTipAmount] = useState<string>('');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState({
    tip: false,
    custom: false
  });

  const subtotalWithTip = useMemo(() => {
    const tip = tipAmount ? parseFloat(tipAmount) : 0;
    return order.total + tip;
  }, [order.total, tipAmount]);

  const amountToPay = useMemo(() => {
    if (method === 'cash' && customAmount && parseFloat(customAmount) > 0) {
      return Math.min(parseFloat(customAmount), subtotalWithTip);
    }
    return subtotalWithTip;
  }, [subtotalWithTip, customAmount, method]);

  const [cashReceived, setCashReceived] = useState<string>('');

  const tipPercentages = [0, 5, 10, 15, 20];

  const remainingAmount = useMemo(() => {
    return Math.max(0, subtotalWithTip - amountToPay);
  }, [subtotalWithTip, amountToPay]);

  useEffect(() => {
    setCashReceived(amountToPay.toString());
  }, [amountToPay]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onSubmit({
      payment_type: method,
      amount: amountToPay,
      tip_amount: tipAmount ? parseFloat(parseFloat(tipAmount).toFixed(2)) : 0,
      custom_amount: remainingAmount,
      received: cashReceived ? parseFloat(cashReceived) : undefined
    });
  };

  const handleTipSelect = (percentage: number) => {
    const tipValue = (order.total * percentage) / 100;
    setTipAmount(tipValue.toFixed(2));
  };

  const calculateChange = () => {
    if (!cashReceived) return 0;
    const received = parseFloat(cashReceived);
    return received >= amountToPay ? received - amountToPay : 0;
  };

  const isFormValid = () => {
    return status === 'idle';
  };

  const toggleSection = (section: 'tip' | 'custom') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-gray-50 dark:bg-dark-bg-lighter p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
            Order Total
          </span>
          <span className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            {formatCurrency(order.total)}
          </span>
        </div>

        {/* Tip Selection */}
        <div>
          <button
            type="button"
            onClick={() => toggleSection('tip')}
            className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
          >
            <span>Add Tip</span>
            <i className={`fas fa-chevron-${expandedSections.tip ? 'up' : 'down'}`} />
          </button>
          {expandedSections.tip && (
            <div className="space-y-2">
              <div className="flex gap-2 mb-2">
                {tipPercentages.map((percentage) => (
                  <button
                    key={percentage}
                    type="button"
                    onClick={() => handleTipSelect(percentage)}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${
                      tipAmount === ((order.total * percentage) / 100).toFixed(2)
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'bg-white dark:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-lighter'
                    } border border-gray-300 dark:border-dark-border`}
                  >
                    {percentage ? `${percentage}%` : 'No Tip'}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-text-secondary">
                  €
                </span>
                <input
                  type="number"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full h-11 pl-7 pr-4 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg-lighter dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          )}
        </div>

        {/* Custom Amount (Cash Only) */}
        {method === 'cash' && (
          <div className="pt-4">
            <button
              type="button"
              onClick={() => toggleSection('custom')}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
            >
              <span>Custom Amount</span>
              <i className={`fas fa-chevron-${expandedSections.custom ? 'up' : 'down'}`} />
            </button>
            {expandedSections.custom && (
              <div className="space-y-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-text-secondary">
                    €
                  </span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setCustomAmount('');
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue <= subtotalWithTip) {
                          setCustomAmount(value);
                        }
                      }
                    }}
                    placeholder="0.00"
                    className="w-full h-11 pl-7 pr-4 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg-lighter dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    step="0.01"
                    min="0"
                    max={subtotalWithTip}
                  />
                </div>
                {customAmount && parseFloat(customAmount) > 0 && (
                  <div className="text-sm text-gray-500 dark:text-dark-text-secondary">
                    <p>Amount to pay now: {formatCurrency(amountToPay)}</p>
                    <p>Amount remaining: {formatCurrency(remainingAmount)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Total with Tip */}
        <div className="pt-4 border-t border-gray-200 dark:border-dark-border">
          <div className="flex justify-between items-center">
            <span className="text-base font-medium text-gray-900 dark:text-dark-text-primary">
              Total to Pay Now
            </span>
            <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
              {formatCurrency(amountToPay)}
            </span>
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={!isFormValid() || status !== 'idle'}
        className="w-full inline-flex justify-center items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 text-white bg-primary-600 hover:bg-primary-700 shadow-md shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'processing' ? (
          <>
            <i className="fas fa-circle-notch fa-spin mr-2" />
            Processing...
          </>
        ) : status === 'completed' ? (
          <>
            <i className="fas fa-check mr-2" />
            Payment Complete
          </>
        ) : (
          <>
            <i className={`fas fa-${method === 'cash' ? 'money-bill-wave' : 'credit-card'} mr-2`} />
            Pay {formatCurrency(amountToPay)}
          </>
        )}
      </button>
    </form>
  );
}